import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  ReceiptLongOutlined as ReceiptLongOutlinedIcon,
} from "@mui/icons-material";
import { AdminAPI } from "../api/admin";
import { format, parseISO } from "date-fns";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BarChart } from '@mui/x-charts';
import { MetricTile, PageHeading, Panel, PortalPage, number, portalColors } from '../components/SuperAdminUI';

function formatDate(value) {
  if (!value) return "N/A";
  try {
    return format(parseISO(value), "MMM dd, yyyy");
  } catch {
    return "N/A";
  }
}

export default function ManagePartners() {
  const navigate = useNavigate();
  const [urlParams] = useSearchParams();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [segment, setSegment] = useState('all');
  const [sort, setSort] = useState('clients_desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerDetails, setPartnerDetails] = useState({ partner: null, enterprises: [] });
  const [createForm, setCreateForm] = useState({ full_name: "", email: "" });
  const [creating, setCreating] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [credentialDialog, setCredentialDialog] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({ bill_to_email: "", billing_period_label: "", amount: "", currency: "INR", due_date: "", description: "Susha platform services", notes: "" });

  const showSnack = useCallback((message, severity = "success") => setSnack({ open: true, message, severity }), []);

  const loadPartners = useCallback(async () => {
    try {
      setLoading(true);
      const res = await AdminAPI.getPartners();
      setPartners(res?.data?.data || []);
    } catch (err) {
      console.error("Load partners error:", err);
      showSnack(err?.response?.data?.error || "Failed to load partners", "error");
    } finally {
      setLoading(false);
    }
  }, [showSnack]);

  useEffect(() => {
    void loadPartners();
  }, [loadPartners]);

  const filteredPartners = useMemo(() => {
    const q = search.toLowerCase();
    return partners.filter((item) =>
      String(item.name || "").toLowerCase().includes(q) ||
      String(item.email || "").toLowerCase().includes(q)
    ).filter((item) => segment === 'all' || (segment === 'with_clients' ? Number(item.enterprise_count || 0) > 0 : Number(item.enterprise_count || 0) === 0)).sort((a, b) => sort === 'name' ? String(a.name || '').localeCompare(String(b.name || '')) : sort === 'recent' ? new Date(b.last_enterprise_added_at || 0) - new Date(a.last_enterprise_added_at || 0) : Number(b.enterprise_count || 0) - Number(a.enterprise_count || 0));
  }, [partners, search, segment, sort]);

  const pagedPartners = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredPartners.slice(start, start + rowsPerPage);
  }, [filteredPartners, page, rowsPerPage]);

  const openDetails = async (row) => {
    setSelectedPartner(row);
    setDetailOpen(true);
    try {
      const [detailRes, invoiceRes] = await Promise.all([AdminAPI.getPartnerDetails(row.id), AdminAPI.getPartnerInvoices(row.id)]);
      const details = detailRes?.data?.data || { partner: null, enterprises: [] };
      setPartnerDetails(details);
      setInvoices(invoiceRes?.data?.data || []);
      setInvoiceForm((current) => ({ ...current, bill_to_email: details.partner?.email || row.email || "" }));
    } catch (err) {
      console.error("Load partner details error:", err);
      setPartnerDetails({ partner: row, enterprises: [] });
      showSnack(err?.response?.data?.error || "Failed to load partner details", "error");
    }
  };

  useEffect(() => {
    const id = Number(urlParams.get('partner'));
    const match = partners.find((partner) => partner.id === id);
    if (match && selectedPartner?.id !== id) void openDetails(match);
    // A link from the overview opens the partner drawer after the list arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partners, urlParams]);

  const sendInvoice = async () => {
    if (!selectedPartner?.id || !invoiceForm.billing_period_label.trim() || !invoiceForm.amount) {
      showSnack("Billing period and invoice amount are required", "warning");
      return;
    }
    try {
      setSendingInvoice(true);
      const res = await AdminAPI.createPartnerInvoice(selectedPartner.id, invoiceForm);
      setInvoices((current) => [res?.data?.data, ...current].filter(Boolean));
      setInvoiceOpen(false);
      setInvoiceForm({ bill_to_email: partnerDetails.partner?.email || "", billing_period_label: "", amount: "", currency: "INR", due_date: "", description: "Susha platform services", notes: "" });
      showSnack(res?.data?.message || "Invoice sent by email", "success");
    } catch (err) {
      showSnack(err?.response?.data?.error || "Unable to send invoice", "error");
    } finally {
      setSendingInvoice(false);
    }
  };

  const handleCreate = async () => {
    if (!createForm.full_name.trim() || !createForm.email.trim()) {
      showSnack("Name and email are required", "warning");
      return;
    }

    try {
      setCreating(true);
      const res = await AdminAPI.createPartner(createForm);
      const payload = res?.data || {};
      setCredentials(payload.credentials || null);
      setCredentialDialog(true);
      setCreateOpen(false);
      setCreateForm({ full_name: "", email: "" });
      await loadPartners();
      showSnack("Partner created successfully", "success");
    } catch (err) {
      console.error("Create partner error:", err);
      showSnack(err?.response?.data?.error || "Failed to create partner", "error");
    } finally {
      setCreating(false);
    }
  };

  const copyText = async (text) => {
    await navigator.clipboard.writeText(String(text || ""));
    showSnack("Copied to clipboard", "success");
  };

  const referralLink = partnerDetails.partner?.referralCode
    ? `${typeof window !== "undefined" ? window.location.origin : ""}/sign-up?ref=${encodeURIComponent(partnerDetails.partner.referralCode)}`
    : "";

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Typography>Loading partners...</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <PortalPage>
      <PageHeading title="Partners" description="Create partner accounts, review client reach and manage Susha invoices for each partner." actions={<><Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadPartners}>Refresh</Button><Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>Add partner</Button></>} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 1.7, mb: 2.5 }}>
        <MetricTile label="Partners" value={number(partners.length)} detail="Partner user accounts" />
        <MetricTile label="With enterprises" value={number(partners.filter((item) => Number(item.enterprise_count) > 0).length)} detail="At least one linked client" accent={portalColors.teal} />
        <MetricTile label="Linked enterprises" value={number(partners.reduce((sum, item) => sum + Number(item.enterprise_count || 0), 0))} detail="Across all partners" />
        <MetricTile label="Active enterprises" value={number(partners.reduce((sum, item) => sum + Number(item.active_enterprise_count || 0), 0))} detail="Current active records" accent={portalColors.green} />
      </Box>
      <Panel title="Partner reach" subtitle="Linked enterprises per partner; linkage is not a resale measure" sx={{ mb: 2.5 }}><Box sx={{ height: 260, p: 1 }}>{partners.length ? <BarChart layout="horizontal" height={245} yAxis={[{ scaleType: 'band', data: [...partners].sort((a, b) => Number(b.enterprise_count || 0) - Number(a.enterprise_count || 0)).slice(0, 6).map((item) => String(item.name || item.email).slice(0, 19)) }]} series={[{ data: [...partners].sort((a, b) => Number(b.enterprise_count || 0) - Number(a.enterprise_count || 0)).slice(0, 6).map((item) => Number(item.enterprise_count || 0)), color: portalColors.teal }]} margin={{ left: 145, right: 25, top: 15, bottom: 25 }} /> : <Typography color="text.secondary" sx={{ p: 4 }}>No partners yet.</Typography>}</Box></Panel>

      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 1.2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search partners..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField select size="small" label="Portfolio" value={segment} onChange={(event) => { setSegment(event.target.value); setPage(0); }} sx={{ minWidth: 145 }}><MenuItem value="all">All partners</MenuItem><MenuItem value="with_clients">With clients</MenuItem><MenuItem value="no_clients">No clients</MenuItem></TextField>
        <TextField select size="small" label="Sort" value={sort} onChange={(event) => setSort(event.target.value)} sx={{ minWidth: 145 }}><MenuItem value="clients_desc">Most clients</MenuItem><MenuItem value="recent">Recently added</MenuItem><MenuItem value="name">Name</MenuItem></TextField>
      </Paper>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell align="right">Enterprises</TableCell>
                <TableCell>Last Enterprise Added On</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedPartners.map((partner) => (
                <TableRow key={partner.id} hover>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Avatar sx={{ width: 32, height: 32 }}>{String(partner.name || partner.email || "P")[0].toUpperCase()}</Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {partner.name || "N/A"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {partner.referral_code || "No referral code"}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{partner.email || "N/A"}</TableCell>
                  <TableCell align="right"><Typography variant="body2" sx={{ fontWeight: 700 }}>{partner.active_enterprise_count || 0}/{partner.enterprise_count || 0}</Typography><Typography variant="caption" color="text.secondary">active / total</Typography></TableCell>
                  <TableCell>{formatDate(partner.last_enterprise_added_at)}</TableCell>
                  <TableCell align="right">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => openDetails(partner)}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {pagedPartners.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                    <Typography variant="body2" color="text.secondary">
                      No partners found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredPartners.length}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Partner</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={createForm.full_name}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, full_name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={createForm.email}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, email: e.target.value }))}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? "Creating..." : "Create Partner"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={credentialDialog} onClose={() => setCredentialDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Partner Credentials</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            Share these credentials with the partner once. The temporary password will not be shown again.
          </Alert>
          <Stack spacing={2}>
            <TextField
              label="Email"
              value={credentials?.email || ""}
              fullWidth
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Temporary Password"
              value={credentials?.password || ""}
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => copyText(credentials?.password)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCredentialDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 560 },
            top: { xs: "56px", sm: "64px" },
            height: { xs: "calc(100% - 56px)", sm: "calc(100% - 64px)" },
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
            {partnerDetails.partner?.name || selectedPartner?.name || "Partner Details"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {partnerDetails.partner?.email || selectedPartner?.email || ""}
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Chip label={`${partnerDetails.enterprises?.length || 0} enterprises`} />
            {partnerDetails.partner?.referralCode && (
              <Chip
                label={`Referral Code: ${partnerDetails.partner.referralCode}`}
                variant="outlined"
              />
            )}
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
            The referral code is the partner onboarding identifier used in referral links.
          </Typography>

          {referralLink ? (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Copyable Register Link
              </Typography>
              <TextField
                fullWidth
                value={referralLink}
                size="small"
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => copyText(referralLink)} edge="end">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Paper>
          ) : null}

          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: "#fbfdff" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
              <Box><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Partner billing</Typography><Typography variant="caption" color="text.secondary">Create a commercial invoice and deliver it through configured SMTP.</Typography></Box>
              <Button size="small" variant="contained" startIcon={<ReceiptLongOutlinedIcon />} onClick={() => setInvoiceOpen(true)}>Create invoice</Button>
            </Stack>
            <Stack spacing={0.75} sx={{ mt: 1.5 }}>
              {invoices.map((invoice) => <Box key={invoice.id} sx={{ display: "flex", justifyContent: "space-between", gap: 1, fontSize: 12.5, py: .6, borderBottom: '1px solid #edf1f7' }}><Typography variant="body2">{invoice.invoice_number} · {invoice.billing_period_label}</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{invoice.currency} {Number(invoice.amount || 0).toLocaleString("en-IN")} · {invoice.status}</Typography></Box>)}
              {!invoices.length && <Typography variant="caption" color="text.secondary">No invoices have been generated for this partner.</Typography>}
            </Stack>
          </Paper>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
            Enterprises and last login
          </Typography>

          <Stack spacing={1.25}>
            {(partnerDetails.enterprises || []).map((enterprise) => (
              <Paper key={enterprise.id} variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center"><Typography variant="body2" sx={{ fontWeight: 700 }}>{enterprise.name}</Typography><Chip size="small" label={enterprise.status || "unknown"} color={String(enterprise.status || "").toLowerCase() === "active" ? "success" : "default"} /></Stack>
                <Button size="small" sx={{ mt: .5 }} onClick={() => navigate(`/superadmin/enterprises/${enterprise.id}`)}>Open enterprise</Button>
                <Typography variant="caption" color="text.secondary">{enterprise.email || "No administrator email"} · Last login: {formatDate(enterprise.last_login_at)}</Typography>
              </Paper>
            ))}
            {(partnerDetails.enterprises || []).length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No enterprises assigned yet.
              </Typography>
            )}
          </Stack>
        </Box>
      </Drawer>

      <Dialog open={invoiceOpen} onClose={() => !sendingInvoice && setInvoiceOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create partner invoice</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2.5 }}>This creates an audit record and sends the invoice to the billing email through SMTP. Configure legal tax details in production before using it as a tax invoice.</Typography>
          <Stack spacing={2}>
            <TextField label="Billing email" type="email" value={invoiceForm.bill_to_email} onChange={(e) => setInvoiceForm((current) => ({ ...current, bill_to_email: e.target.value }))} fullWidth required />
            <TextField label="Billing period" placeholder="September 2026" value={invoiceForm.billing_period_label} onChange={(e) => setInvoiceForm((current) => ({ ...current, billing_period_label: e.target.value }))} fullWidth required />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}><TextField label="Amount" type="number" inputProps={{ min: 0, step: "0.01" }} value={invoiceForm.amount} onChange={(e) => setInvoiceForm((current) => ({ ...current, amount: e.target.value }))} fullWidth required /><TextField label="Currency" value={invoiceForm.currency} onChange={(e) => setInvoiceForm((current) => ({ ...current, currency: e.target.value.toUpperCase() }))} inputProps={{ maxLength: 3 }} fullWidth /><TextField label="Due date" type="date" value={invoiceForm.due_date} onChange={(e) => setInvoiceForm((current) => ({ ...current, due_date: e.target.value }))} InputLabelProps={{ shrink: true }} fullWidth /></Stack>
            <TextField label="Line item description" value={invoiceForm.description} onChange={(e) => setInvoiceForm((current) => ({ ...current, description: e.target.value }))} fullWidth />
            <TextField label="Internal note or payment instruction" value={invoiceForm.notes} onChange={(e) => setInvoiceForm((current) => ({ ...current, notes: e.target.value }))} multiline minRows={2} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions><Button disabled={sendingInvoice} onClick={() => setInvoiceOpen(false)}>Cancel</Button><Button variant="contained" disabled={sendingInvoice} onClick={sendInvoice}>{sendingInvoice ? "Sending..." : "Generate and send invoice"}</Button></DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </PortalPage>
  );
}
